import { StrictMode, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { postFocalPoint } from "../js/api/member-media/focal-point";
import { FocalPointPicker, toPoint } from "./components/shared/focal-point-picker";
import { Dialog, DialogContent, DialogTitle } from "./components/shared/modal";

/** The shapes a collection image is cut to: the collection card, and the wide share/feature image. */
const COLLECTION_PREVIEWS = [
  { label: "Collection card", aspect: 264 / 340 },
  { label: "Wide", aspect: 16 / 9 },
];

/**
 * "Adjust framing" for a colour collection's image, on the Razor-rendered collection page
 * (Views/CollectionDetail.cshtml). A thin wrapper: the picker is the shared one, the save is the
 * shared endpoint with source "collection", and on success the page reloads so every
 * server-rendered crop of the image picks the new point up.
 */
function CollectionFocalPoint({ collectionId, mediaKey, src, initialPoint, copy }) {
  const [isOpen, setIsOpen] = useState(false);
  const [point, setPoint] = useState(initialPoint);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const openButtonRef = useRef(null);

  const close = () => {
    setIsOpen(false);
    setPoint(initialPoint);
    setError(null);
  };

  const save = async () => {
    setIsSaving(true);
    setError(null);

    const result = await postFocalPoint({
      source: "collection",
      id: collectionId,
      mediaKey,
      left: point.left,
      top: point.top,
    });

    if (!result.isSuccess) {
      setIsSaving(false);
      setError(result.message);
      return;
    }

    window.location.reload();
  };

  return (
    <div className="collection-focal-point">
      <button
        ref={openButtonRef}
        type="button"
        className="collection-focal-point__open"
        onClick={() => setIsOpen(true)}
        data-testid="collection-image-adjust-framing"
      >
        {copy.open}
      </button>

      {/* The dialog the cover uses, not an inline panel: the image sits in a card-width column of
          fixed height, and an inline panel spilled out of it underneath the page footer. */}
      {isOpen ? (
        <Dialog open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : close())}>
          <DialogContent aria-describedby={undefined}>
            <div className="hero-image__header">
              <DialogTitle className="hero-image__title">{copy.title}</DialogTitle>
            </div>

            <div className="hero-image__main" data-testid="collection-image-framing">
              <FocalPointPicker
                src={src}
                alt=""
                value={point}
                onChange={setPoint}
                previews={COLLECTION_PREVIEWS}
                disabled={isSaving}
                testId="collection-image-focal-point"
              />

              {error ? (
                <p className="focal-point-error" role="alert" data-testid="collection-image-framing-error">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="hero-image__footer">
              <div className="hero-image__buttons-wrapper">
                <button type="button" className="my-profile__submit-button" onClick={close} disabled={isSaving}>
                  {copy.cancel}
                </button>
                <button
                  type="button"
                  className="my-profile__submit-button my-profile__submit-button--dark"
                  onClick={save}
                  disabled={isSaving}
                  data-testid="collection-image-framing-save"
                >
                  {isSaving ? copy.saving : copy.save}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function render() {
  const root = document.querySelector(".js-collection-focal-point");

  if (!root) {
    return;
  }

  const { collectionId, mediaKey, src, focalLeft, focalTop } = root.dataset;

  createRoot(root).render(
    <StrictMode>
      <CollectionFocalPoint
        collectionId={Number(collectionId)}
        mediaKey={mediaKey || null}
        src={src}
        initialPoint={toPoint(focalLeft, focalTop)}
        copy={{
          open: root.dataset.copyOpen || "Adjust framing",
          title: root.dataset.copyTitle || "Adjust image framing",
          save: root.dataset.copySave || "Save framing",
          saving: root.dataset.copySaving || "Saving…",
          cancel: root.dataset.copyCancel || "Cancel",
        }}
      />
    </StrictMode>
  );
}

export const initCollectionFocalPoint = () => {
  if (document.readyState !== "loading") {
    render();
  } else {
    document.addEventListener("DOMContentLoaded", render);
  }
};
